import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhygitalIcon from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  PartyPopper,
  Calendar,
  Users,
  Sparkles,
  MapPin,
  Clock,
  Globe,
  Image as ImageIcon,
  Mic,
  Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pilot types - determines dashboard style
const PILOT_TYPES = [
  { 
    id: "event", 
    name: "Event", 
    desc: "Full event management with RSVPs, dietary needs, accessibility",
    icon: PartyPopper,
    color: "bg-amber-100 text-amber-700 border-amber-200",
    features: ["RSVP Management", "Dietary Tracking", "Accessibility", "Check-in", "Guest List"]
  },
  { 
    id: "instructor", 
    name: "Instructor Session", 
    desc: "Workshop or class with audience targeting and interest matching",
    icon: Mic,
    color: "bg-purple-100 text-purple-700 border-purple-200",
    features: ["Audience Targeting", "Interest Matching", "Q&A", "Attendee Insights", "Question Bank"]
  }
];

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // URL params
  const orgId = searchParams.get("org_id") || searchParams.get("org");
  const orgName = searchParams.get("org_name") || "";
  const pilotId = searchParams.get("pilot_id") || "";
  
  // Form state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [pilots, setPilots] = useState([]);
  
  // Event data
  const [selectedOrg, setSelectedOrg] = useState(orgId || "");
  const [selectedPilot, setSelectedPilot] = useState(pilotId || "");
  const [pilotType, setPilotType] = useState("event");
  const [eventName, setEventName] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [timeTBA, setTimeTBA] = useState(false);
  const [location, setLocation] = useState("");
  const [isVirtual, setIsVirtual] = useState(false);
  const [maxGuests, setMaxGuests] = useState(50);
  const [isDraft, setIsDraft] = useState(true);
  
  // Fetch user's organizations and pilots
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        
        // Fetch organizations the user can host under
        const orgRes = await fetch(`${API}/me/hosting`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (orgRes.ok) {
          const data = await orgRes.json();
          setOrganizations(data.organizations || []);
          
          // Extract pilots from organizations
          const allPilots = (data.organizations || []).flatMap(org => 
            (org.pilots || []).map(p => ({ ...p, org_id: org.org_id, org_name: org.name }))
          );
          setPilots(allPilots);
          
          // Auto-select org if passed in URL
          if (orgId && data.organizations?.find(o => o.org_id === orgId)) {
            setSelectedOrg(orgId);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    
    fetchData();
  }, [orgId]);
  
  // Get selected org details
  const selectedOrgData = organizations.find(o => o.org_id === selectedOrg);
  
  // Get pilots for selected org
  const orgPilots = pilots.filter(p => p.org_id === selectedOrg);
  
  // Create the event
  const handleCreate = async () => {
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }
    
    if (!selectedOrg) {
      toast.error("Please select an organization");
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      
      const eventData = {
        name: eventName,
        description: eventDescription,
        org_id: selectedOrg,
        pilot_id: selectedPilot || null,
        pilot_type: pilotType,
        date: eventDate || null,
        time: timeTBA ? null : eventTime,
        time_tba: timeTBA,
        location: isVirtual ? "Virtual" : location,
        is_virtual: isVirtual,
        max_guests: parseInt(maxGuests) || 50,
        status: isDraft ? "draft" : "active"
      };
      
      const res = await fetch(`${API}/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${pilotType === "instructor" ? "Session" : "Event"} created!`);
        
        // Navigate to the appropriate dashboard
        navigate(`/host/v2/${data.event_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create event");
      }
    } catch (err) {
      toast.error("Something went wrong");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <PhygitalIcon className="w-8 h-8" />
          <div className="w-16" /> {/* Spacer */}
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-500"
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-1 rounded ${step > s ? "bg-amber-500" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>
        
        {/* Step 1: Choose Type */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">What are you creating?</h1>
              <p className="text-gray-600 mt-2">Choose the type of experience you want to host</p>
            </div>
            
            <div className="grid gap-4">
              {PILOT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = pilotType === type.id;
                return (
                  <Card 
                    key={type.id}
                    className={`cursor-pointer transition-all ${
                      isSelected 
                        ? "ring-2 ring-amber-500 border-amber-300" 
                        : "hover:border-gray-300"
                    }`}
                    onClick={() => setPilotType(type.id)}
                    data-testid={`pilot-type-${type.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${type.color}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{type.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{type.desc}</p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {type.features.map((f) => (
                              <Badge key={f} variant="secondary" className="text-xs">
                                {f}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <Button 
              className="w-full mt-6" 
              size="lg"
              onClick={() => setStep(2)}
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
        
        {/* Step 2: Organization & Pilot */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Link to Organization</h1>
              <p className="text-gray-600 mt-2">Select which organization this belongs to</p>
            </div>
            
            {/* Organization Selection */}
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger data-testid="org-select">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.org_id} value={org.org_id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {org.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pilot Selection (optional) */}
            {selectedOrg && orgPilots.length > 0 && (
              <div className="space-y-2">
                <Label>Link to Pilot (optional)</Label>
                <Select value={selectedPilot} onValueChange={setSelectedPilot}>
                  <SelectTrigger data-testid="pilot-select">
                    <SelectValue placeholder="Select pilot or leave blank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No pilot - standalone {pilotType}</SelectItem>
                    {orgPilots.map((pilot) => (
                      <SelectItem key={pilot.profile_id} value={pilot.profile_id}>
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          {pilot.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Linking to a pilot applies that pilot's defaults and settings
                </p>
              </div>
            )}
            
            {/* Selected org info */}
            {selectedOrgData && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{selectedOrgData.name}</p>
                      <p className="text-sm text-gray-600">
                        {selectedOrgData.pilots?.length || 0} pilots • {selectedOrgData.events?.length || 0} events
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!selectedOrg}
                className="flex-1"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Event Details */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">
                {pilotType === "instructor" ? "Session Details" : "Event Details"}
              </h1>
              <p className="text-gray-600 mt-2">Fill in the basics - you can edit everything later</p>
            </div>
            
            {/* Name */}
            <div className="space-y-2">
              <Label>{pilotType === "instructor" ? "Session Name" : "Event Name"} *</Label>
              <Input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder={pilotType === "instructor" ? "e.g., Hair Color Masterclass" : "e.g., Summer Mixer 2026"}
                data-testid="event-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder="Brief description..."
                rows={3}
              />
            </div>
            
            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  Time
                  <span className="text-xs font-normal flex items-center gap-2">
                    <Switch 
                      checked={timeTBA} 
                      onCheckedChange={setTimeTBA}
                      className="scale-75"
                    />
                    TBA
                  </span>
                </Label>
                <Input
                  type="time"
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                  disabled={timeTBA}
                  className={timeTBA ? "opacity-50" : ""}
                />
              </div>
            </div>
            
            {/* Location */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                Location
                <span className="text-xs font-normal flex items-center gap-2">
                  <Switch 
                    checked={isVirtual} 
                    onCheckedChange={setIsVirtual}
                    className="scale-75"
                  />
                  Virtual
                </span>
              </Label>
              {!isVirtual ? (
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Venue name or address"
                />
              ) : (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-700">Virtual event - link will be shared with attendees</span>
                </div>
              )}
            </div>
            
            {/* Capacity */}
            <div className="space-y-2">
              <Label>Capacity</Label>
              <Input
                type="number"
                value={maxGuests}
                onChange={(e) => setMaxGuests(e.target.value)}
                min={1}
                max={10000}
              />
              <p className="text-xs text-gray-500">Maximum number of attendees</p>
            </div>
            
            {/* Draft toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Save as Draft</p>
                <p className="text-sm text-gray-600">Keep private until you're ready to publish</p>
              </div>
              <Switch checked={isDraft} onCheckedChange={setIsDraft} />
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={loading || !eventName.trim()}
                className="flex-1"
                data-testid="create-event-btn"
              >
                {loading ? "Creating..." : `Create ${pilotType === "instructor" ? "Session" : "Event"}`}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateEventPage;
